// src/private/src/pages/app/clients/components/MetricCell.jsx
import React, { memo } from 'react';
import { Box, TableCell, Typography } from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

// ─── Threshold configurations ─────────────────────────────────────────────────
// Each config defines thresholds and whether higher is better or worse.
// Colors are applied as subtle cell backgrounds.

const METRIC_THRESHOLDS = {
  // Rates where HIGHER is better
  setRate: { good: 0.25, warn: 0.15, direction: 'higher' },
  issueRate: { good: 0.65, warn: 0.50, direction: 'higher' },
  demoRate: { good: 0.80, warn: 0.65, direction: 'higher' },
  netCloseRate: { good: 0.35, warn: 0.20, direction: 'higher' },
  acceptedRate: { good: 0.80, warn: 0.60, direction: 'higher' },
  successRate: { good: 0.80, warn: 0.60, direction: 'higher' },

  // Rates where LOWER is better
  outOfAreaRate: { good: 0.05, warn: 0.12, direction: 'lower' },
  costOfMarketing: { good: 0.20, warn: 0.30, direction: 'lower' },
};

const COLORS = {
  good: { bg: 'rgba(34, 197, 94, 0.10)', text: '#16a34a' },
  warn: { bg: 'rgba(245, 158, 11, 0.12)', text: '#d97706' },
  bad: { bg: 'rgba(239, 68, 68, 0.10)', text: '#dc2626' },
  neutral: { bg: 'transparent', text: 'inherit' },
};

/**
 * Determine color tier for a metric value based on its threshold config.
 * Returns 'good' | 'warn' | 'bad' | 'neutral'.
 */
export function getMetricTier(metricKey, value) {
  const config = METRIC_THRESHOLDS[metricKey];
  if (!config) return 'neutral';

  const num = Number(value);
  if (!Number.isFinite(num)) return 'neutral';

  if (config.direction === 'higher') {
    if (num >= config.good) return 'good';
    if (num >= config.warn) return 'warn';
    return 'bad';
  }
  // direction === 'lower' — lower is better
  if (num <= config.good) return 'good';
  if (num <= config.warn) return 'warn';
  return 'bad';
}

// 1 percentage point deadband — differences smaller than this read as neutral
// instead of green/red, to avoid flickering on rounding noise.
const REFERENCE_DEADBAND = 0.01;

/**
 * Tier a metric based on how it compares to a reference value (e.g., the peer
 * cohort average for the same vertical). Honors the metric's direction config:
 * for higher-is-better metrics, above-reference = good; for lower-is-better,
 * below-reference = good.
 */
export function getMetricTierVsReference(metricKey, value, reference) {
  const num = Number(value);
  const ref = Number(reference);
  if (!Number.isFinite(num) || !Number.isFinite(ref)) return 'neutral';

  const direction = METRIC_THRESHOLDS[metricKey]?.direction || 'higher';
  const delta = num - ref;
  if (Math.abs(delta) < REFERENCE_DEADBAND) return 'neutral';
  if (direction === 'higher') {
    return delta > 0 ? 'good' : 'bad';
  }
  return delta < 0 ? 'good' : 'bad';
}

/**
 * Get the sx style object for a metric cell based on its tier. When a
 * `reference` value is provided, tier is computed by direction-aware delta
 * against the reference instead of against the generic threshold config.
 */
export function getMetricCellSx(metricKey, value, reference) {
  const tier = reference !== undefined && reference !== null
    ? getMetricTierVsReference(metricKey, value, reference)
    : getMetricTier(metricKey, value);
  const color = COLORS[tier];
  if (tier === 'neutral') return {};
  return {
    backgroundColor: color.bg,
    color: color.text,
    fontWeight: tier !== 'neutral' ? 600 : undefined,
  };
}

// ─── Delta display ────────────────────────────────────────────────────────────

/**
 * Small inline delta indicator showing change vs previous period.
 * Shows an arrow + percentage change. Green = improvement, red = decline.
 *
 * @param {number} current - Current period value
 * @param {number} previous - Previous period value
 * @param {'higher'|'lower'} goodDirection - Whether higher or lower is better
 *   Defaults to 'higher' (more leads = good). Use 'lower' for cost metrics (lower CPL = good).
 * @param {'percent'|'absolute'} format - How to display the delta
 */
function DeltaIndicator({ current, previous, goodDirection = 'higher', format = 'percent' }) {
  const cur = Number(current);
  const prev = Number(previous);

  if (!Number.isFinite(cur) || !Number.isFinite(prev) || prev === 0) {
    return null;
  }

  const change = cur - prev;
  const pctChange = (change / Math.abs(prev)) * 100;

  if (!Number.isFinite(pctChange) || Math.abs(pctChange) < 0.5) {
    return null;
  }

  const isPositive = change > 0;
  const isGood = goodDirection === 'higher' ? isPositive : !isPositive;

  const color = isGood ? '#16a34a' : '#dc2626';
  const ArrowIcon = isPositive ? ArrowUpwardIcon : ArrowDownwardIcon;
  const displayValue =
    format === 'percent'
      ? `${Math.abs(pctChange).toFixed(1)}%`
      : Math.abs(change).toLocaleString('en-US');

  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.15,
        ml: 0.5,
        fontSize: '0.65rem',
        fontWeight: 600,
        color,
        whiteSpace: 'nowrap',
      }}
    >
      <ArrowIcon sx={{ fontSize: '0.7rem' }} />
      {displayValue}
    </Box>
  );
}

/**
 * A table cell wrapper that applies conditional coloring and optionally
 * shows a period-over-period delta.
 *
 * Usage:
 *   <MetricCell metricKey="setRate" value={0.22} prevValue={0.18}>
 *     <PercentageFormatter value={0.22} />
 *   </MetricCell>
 */
function MetricCell({
  metricKey,
  value,
  prevValue,
  reference,
  goodDirection,
  deltaFormat = 'percent',
  showDelta = false,
  sx: sxProp,
  children,
  ...rest
}) {
  const tierSx = getMetricCellSx(metricKey, value, reference);
  // Infer good direction from threshold config if not explicitly set
  const resolvedDirection =
    goodDirection || METRIC_THRESHOLDS[metricKey]?.direction || 'higher';

  return (
    <TableCell sx={{ ...tierSx, ...sxProp }} {...rest}>
      {children}
      {showDelta && prevValue !== undefined && (
        <DeltaIndicator
          current={value}
          previous={prevValue}
          goodDirection={resolvedDirection}
          format={deltaFormat}
        />
      )}
    </TableCell>
  );
}

/**
 * Inline delta for use in hero stat cards (not inside a TableCell).
 */
export function StatDelta({ current, previous, goodDirection = 'higher', format = 'percent' }) {
  return (
    <DeltaIndicator
      current={current}
      previous={previous}
      goodDirection={goodDirection}
      format={format}
    />
  );
}

export default memo(MetricCell);
